// components/ZarinpalCallback.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import BASE_URL from "../../../../config/config";

const ZarinpalCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('در حال بررسی پرداخت...');
  const [orderData, setOrderData] = useState(null);
  
  // 🔥 استفاده از useRef برای جلوگیری از اجرای مکرر
  const hasVerified = useRef(false);

  useEffect(() => {
    // 🔥 اگر قبلاً verify شده، دیگه اجرا نمیشه
    if (hasVerified.current) return;

    const verifyPayment = async () => {
      const authority = searchParams.get('Authority');
      const paymentStatus = searchParams.get('Status');

      // ❌ اگر پرداخت لغو شده
      if (paymentStatus !== 'OK' || !authority) {
        setStatus('error');
        setMessage('پرداخت لغو شد یا ناموفق بود');
        setTimeout(() => {
          navigate('/consultation/failed', { replace: true });
        }, 3000);
        return;
      }

      try {
        // 🔥 علامت‌گذاری که verify شروع شد
        hasVerified.current = true;

        // 🔹 ارسال درخواست Verify به بک‌اند
        const response = await fetch(
          `${BASE_URL}/consultation/verify?Authority=${authority}&Status=${paymentStatus}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            // ✅ حذف credentials برای جلوگیری از CORS error
          }
        );

        const data = await response.json();

        // ✅ بررسی موفقیت بر اساس response جدید بک‌اند
        if (response.ok && data.success) {
          setStatus('success');
          setMessage('پرداخت با موفقیت تأیید شد');
          setOrderData({
            orderId: data.orderId,
            refId: data.refId,
            cardPan: data.cardPan
          });

          // 🔹 هدایت به صفحه موفقیت پس از 2 ثانیه
          setTimeout(() => {
            navigate(
              `/consultation/success?orderId=${data.orderId}&refId=${data.refId}`,
              { replace: true }
            );
          }, 2000);
        } else {
          throw new Error(data.message || 'خطا در تأیید پرداخت');
        }
      } catch (error) {
        console.error('❌ خطای Verify:', error);
        setStatus('error');
        setMessage(error.message || 'خطا در تأیید پرداخت. لطفاً با پشتیبانی تماس بگیرید');
        
        // هدایت به صفحه خطا پس از 3 ثانیه
        setTimeout(() => {
          navigate('/consultation/failed', { replace: true });
        }, 3000);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]); // ✅ hasVerified از dependencies حذف شد

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        
        {/* Loading */}
        {status === 'loading' && (
          <>
            <div className="mb-6 flex justify-center">
              <Loader2 className="animate-spin text-blue-500" size={64} />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              {message}
            </h2>
            <p className="text-slate-600">
              لطفاً این صفحه را نبندید یا رفرش نکنید
            </p>
          </>
        )}

        {/* Success */}
        {status === 'success' && (
          <>
            <div className="mb-6 flex justify-center">
              <CheckCircle className="text-green-500" size={64} />
            </div>
            <h2 className="text-2xl font-bold text-green-600 mb-3">
              پرداخت موفق! ✅
            </h2>
            <p className="text-slate-700 mb-4">{message}</p>
            {orderData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-right">
                <p className="text-sm text-slate-600">شماره سفارش:</p>
                <p className="text-lg font-mono font-bold text-slate-800">
                  {orderData.orderId}
                </p>
                <p className="text-sm text-slate-600 mt-2">کد رهگیری:</p>
                <p className="text-lg font-mono font-bold text-slate-800">
                  {orderData.refId}
                </p>
              </div>
            )}
            <p className="text-slate-500 text-sm mt-4">
              در حال هدایت به صفحه موفقیت...
            </p>
          </>
        )}

        {/* Error */}
        {status === 'error' && (
          <>
            <div className="mb-6 flex justify-center">
              <XCircle className="text-red-500" size={64} />
            </div>
            <h2 className="text-2xl font-bold text-red-600 mb-3">
              خطا در پرداخت ❌
            </h2>
            <p className="text-slate-700 mb-4">{message}</p>
            <p className="text-slate-500 text-sm">
              در حال بازگشت...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ZarinpalCallback;
